'use client';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole = 'admin' | 'staff' | 'client';

interface RoleContextValue {
  role: UserRole;
  setRole: (role: UserRole) => void;
  isAdmin: boolean;
  isStaff: boolean;
  isClient: boolean;
  canSwitchRole: boolean;
  roleLoading: boolean;
}

const RoleContext = createContext<RoleContextValue>({
  role: 'staff',
  setRole: () => {},
  isAdmin: false,
  isStaff: true,
  isClient: false,
  canSwitchRole: false,
  roleLoading: true,
});

export function RoleProvider({ children }: { children: ReactNode }) {
  const [role, setRoleState] = useState<UserRole>('staff');
  const [isAdminUser, setIsAdminUser] = useState<boolean>(false);
  const [roleLoading, setRoleLoading] = useState<boolean>(true);

  // Bootstrap role from Supabase auth session on mount
  useEffect(() => {
    const supabase = createClient();

    const resolveRole = (session: any): UserRole => {
      if (!session?.user) return 'staff';
      const meta = session.user.user_metadata ?? {};
      const appMeta = session.user.app_metadata ?? {};
      const rawRole = meta.role ?? appMeta.role ?? '';
      if (rawRole === 'admin') return 'admin';
      if (rawRole === 'client') return 'client';
      // Also check staff_accounts via email pattern if needed
      return 'staff';
    };

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const resolvedRole = resolveRole(session);
      setRoleState(resolvedRole);
      if (resolvedRole === 'admin') {
        setIsAdminUser(true);
      }
      setRoleLoading(false);
    });

    // Listen for auth state changes (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const resolvedRole = resolveRole(session);
      setRoleState(resolvedRole);
      if (resolvedRole === 'admin') {
        setIsAdminUser(true);
      } else if (!session) {
        // Logged out — reset
        setIsAdminUser(false);
      }
      setRoleLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Only admin is allowed to switch roles
  const handleSetRole = (newRole: UserRole) => {
    if (role === 'admin' || isAdminUser) {
      // Remember they are an admin even when viewing as staff
      if (role === 'admin') {
        setIsAdminUser(true);
      }
      setRoleState(newRole);
    }
    // staff and client cannot switch roles — silently ignore
  };

  // canSwitchRole is true if the current user IS an admin (even when viewing as staff)
  const canSwitchRole = role === 'admin' || isAdminUser;

  return (
    <RoleContext.Provider
      value={{
        role,
        setRole: handleSetRole,
        isAdmin: role === 'admin',
        isStaff: role === 'staff' || role === 'admin',
        isClient: role === 'client',
        canSwitchRole,
        roleLoading,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  return useContext(RoleContext);
}
